import { type Cohort, getRequiredStreamPackIdsFromPeriods } from 'Cohorts';
import { getProgramConfigValue } from 'Program';
import { streamDashboardPath } from 'Lessons';
import { stemmedWordsSet } from 'Porter-stemmer';
import { type BrowseStream, type ProgressStatus } from './BrowseCourses.types';

// Each selector mirrors an Iguana Stream getter. selectorsMatchIguana.spec.ts keeps them in sync.

// Stream#contentTopicNames
export function getContentTopicNames(stream: BrowseStream, localeCode: string): string[] {
    return (stream.locale_pack.content_topics ?? []).map(topic => topic.locales[localeCode] || topic.locales.en);
}

// Stream#progressStatus. A timed stream whose limit ran out before completion is not in progress.
export function getProgressStatus(stream: BrowseStream, now: number): ProgressStatus {
    const progress = stream.lesson_streams_progress;
    if (!progress) return 'not_started';
    if (progress.complete) return 'completed';

    const timeRanOut = !!progress.time_runs_out_at && progress.time_runs_out_at * 1000 <= now;
    if (!timeRanOut && progress.started_at) return 'in_progress';

    return 'not_started';
}

// Stream#lessonsCompletedCount
export function getLessonsCompletedCount(stream: BrowseStream): number {
    return stream.lessons.filter(lesson => !!lesson.lesson_progress?.complete).length;
}

// Stream#elective. `cohort` is the user's relevant cohort. Cohort#getRequiredStreamPackIdsFromPeriods
// returns no ids for a cohort without a schedule, so every non-exam stream is elective there.
export function isElective(stream: BrowseStream, cohort: Cohort | null): boolean {
    if (stream.exam || !cohort) return false;
    if (!getProgramConfigValue(cohort.programType, 'supportsSchedule')) return true;
    return !getRequiredStreamPackIdsFromPeriods(cohort).includes(stream.locale_pack.id);
}

// Stream#streamDashboardPath
export function getStreamPath(stream: BrowseStream): string {
    return (
        stream.entity_metadata?.canonical_url || streamDashboardPath({ streamTitle: stream.title, streamId: stream.id })
    );
}

// The stemmed search terms of the stream, its chapters, topics, and lessons, joined into one string for
// substring searches.
export function getSearchIndex(stream: BrowseStream, localeCode: string, searchLocale?: string): string {
    const texts: (string | null | undefined)[] = [
        stream.title,
        stream.description,
        ...stream.chapters.map(chapter => chapter.title),
        ...getContentTopicNames(stream, localeCode),
    ];
    const terms = stemmedWordsSet(texts, searchLocale);

    stream.lessons.forEach(lesson => {
        const lessonTexts = [lesson.title, ...(lesson.description ?? []), ...(lesson.key_terms ?? [])];
        stemmedWordsSet(lessonTexts, searchLocale).forEach(term => terms.add(term));
    });

    return [...terms].join(' ');
}
